/*
 * The sign-up's one door to Firebase. The screen and /next-step import only
 * this module (tests mock it). It loads the Firebase SDK on first use, and only
 * when the build has the Firebase variables (config.js); otherwise every call
 * rejects with code "not-configured" and nothing is loaded.
 */
import { isFirebaseConfigured } from './config';

export { isFirebaseConfigured };
export { CONSENT_TEXT, verificationErrorMessage, emailErrorMessage } from './messages';

let clientPromise = null;

function client() {
  if (!isFirebaseConfigured) {
    const error = new Error('Firebase is not configured');
    error.code = 'not-configured';
    return Promise.reject(error);
  }
  if (!clientPromise) clientPromise = import('./firebaseClient');
  return clientPromise;
}

export const sendSmsCode = async (phoneE164, recaptchaId) => (await client()).sendSmsCode(phoneE164, recaptchaId);
export const confirmSmsCode = async (confirmation, code) => (await client()).confirmSmsCode(confirmation, code);
export const sendEmailCode = async (details) => (await client()).sendEmailCode(details);
export const confirmEmailCode = async (email, code) => (await client()).confirmEmailCode(email, code);
export const createLead = async (fields) => (await client()).createLead(fields);
export const updateLead = async (fields) => (await client()).updateLead(fields);

/*
 * /next-step "Confirm demo request": for a visitor verified on this browser,
 * stamp the lead as finished and ask the deployed sendLeadEmail function to
 * email the team (sai's finishLead + notifyTeam). Resolves to { sent } —
 * false when Firebase is not set up or the visitor never verified (the
 * "Continue to the demo" path), so nothing can be saved. Rejects only when a
 * verified visitor's save fails.
 */
export async function requestDemo() {
  if (!isFirebaseConfigured) return { sent: false };
  const c = await client();
  if (!(await c.isVerified())) return { sent: false };
  await c.finishLead({
    demoFinished: true,
    closing: { priority: null, callRequested: true, callTime: null },
  });
  await c.notifyTeam();
  return { sent: true };
}

/*
 * Log out: ends the visitor's Firebase session, when this build has one.
 * Never rejects: the sign-up flag is cleared by the caller either way.
 */
export async function signOutVisitor() {
  if (!isFirebaseConfigured) return;
  try {
    await (await client()).signOutVisitor();
  } catch {
    // Already signed out, or the SDK failed to load: nothing to end.
  }
}

/*
 * The guard's second check (App.js): does Firebase hold a verified visitor on
 * this browser? Resolves true / false; true when the build has no Firebase
 * settings (nobody can be signed up there, so the flag alone decides), and
 * true when the check itself cannot run (offline), so a verified visitor is
 * not thrown out by a network blip.
 */
export async function isVerified() {
  if (!isFirebaseConfigured) return true;
  try {
    return await (await client()).isVerifiedNow();
  } catch {
    return true;
  }
}
